// Il REST di QChinesePoker dal BFF di Limulo (T1b; condizioni U0-U6 di tma-orcb, contratto `contratto-qcp-bff-t1.md` sez. B e C).
// Funzioni PURE, niente rete: le query e le mutation stanno in `services/limuloApi.ts`, i consumatori in pannelli e fogli.
//
// Forma presa dal CODICE del resolver, non dai mock:
//  · ogni risposta ha la DOPPIA busta `{ ok, organizationId, data: { provider, [count], data: <corpo QCP> }, meta }`;
//  · ⚠️ le LISTE (classifica, storico) hanno `data.data` = ARRAY, e `capabilityPayloadOf` scende solo per gli OGGETTI: per le
//    liste c'e' `qcpListOf`, che non e' una variante cosmetica;
//  · gli errori: `code` porta il codice, `error` il messaggio. Il BFF appiattisce ogni 4xx in `E_QLASH_HTTP_<n>` e ogni guasto in
//    `424 E_QLASH_CAPABILITY_RESOLVER_ERROR`: l'UNICO 424 che vuol dire «non c'e'» e' `E_QLASH_CAPABILITY_DISABLED`.
// Un corpo che non ha la forma attesa e' `null` (contratto violato = errore con «Riprova»), mai un elenco vuoto o un pezzo di elenco.

const BASE = '/v1/games/chinesepoker';
export const QCP_REST = {
  me: `${BASE}/me`,
  leaderboard: `${BASE}/leaderboard`,
  myMatches: `${BASE}/matches/mine`,
  match: (id: string) => `${BASE}/matches/${encodeURIComponent(id)}`,
  replay: (id: string) => `${BASE}/matches/${encodeURIComponent(id)}/replay`,
  invites: `${BASE}/invites`,
  invite: (code: string) => `${BASE}/invites/${encodeURIComponent(code)}`,
  accept: (code: string) => `${BASE}/invites/${encodeURIComponent(code)}/accept`,
} as const;

/* ── Tipi (a mano da QChinesePoker/packages/qcp-db/src/types.ts; `tournamentId`/`tournamentSlot` non si leggono) ── */
export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  elo: number;
  rank: number;
}
type Seat = 'A' | 'B';
export interface MatchSummary {
  matchId: string;
  players: Record<Seat, { userId: string; displayName: string }>;
  net: Record<Seat, number>;
  winnerSeat: Seat | null;
  reason: string;
  finishedAt: number; // epoch ms
}
type Cards = Array<{ r: number; s: 'c' | 'd' | 'h' | 's' }>;
export interface HandRecord {
  handNumber: number;
  netA: number;
  netB: number;
  commitHash: string;
  /** null on forfeited hands (clock-flag / resign): never dealt or completed. */
  serverSeed: string | null;
  deckOrder: Cards | null;
  boards: Record<Seat, { front: Cards; middle: Cards; back: Cards }> | null;
  discards: Record<Seat, Cards> | null;
}
export interface MatchReplay extends MatchSummary {
  setLength: number;
  hands: HandRecord[];
}
/** `expiresAt` e' un NUMERO (ms epoch): il server lo manda cosi', e `Date.parse(numero)` da' NaN. */
export interface QcpInvite {
  code: string;
  expiresAt: number;
}
export interface QcpInvitePreview {
  creator: { displayName: string; elo: number };
  expiresAt: number;
}
export interface QcpInviteAccepted {
  matchId: string;
  seat: Seat;
}
/** Chi sono io per QCP (`chinesepoker.me.read`). `userId` e' lo `User.id` di QCP (uuid), NON il `sub` Logto; `null` = nessun profilo. */
export interface QcpMe {
  hasProfile: boolean;
  userId: string | null;
}

/* ── Lettori ── */
const rec = (v: unknown): Record<string, unknown> => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const str = (v: unknown): v is string => typeof v === 'string' && v !== '';
const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const seatOf = (v: unknown): v is Seat => v === 'A' || v === 'B';
const player = (v: unknown): boolean => str(rec(v).userId) && typeof rec(v).displayName === 'string';
const pair = (v: unknown, ok: (x: unknown) => boolean): boolean => ok(rec(v).A) && ok(rec(v).B);

const isEntry = (r: unknown): r is LeaderboardEntry => str(rec(r).userId) && typeof rec(r).displayName === 'string' && num(rec(r).elo) && num(rec(r).rank);
const isSummary = (r: unknown): r is MatchSummary => {
  const o = rec(r);
  return str(o.matchId) && pair(o.players, player) && pair(o.net, num) && (o.winnerSeat === null || seatOf(o.winnerSeat)) && typeof o.reason === 'string' && num(o.finishedAt);
};
const isHand = (r: unknown): r is HandRecord => num(rec(r).handNumber) && num(rec(r).netA) && num(rec(r).netB) && typeof rec(r).commitHash === 'string';

/**
 * L'elenco dal `data` del BFF (`unwrapData`): `{ provider, count, data: [...] }`. Si accetta anche l'array nudo. Una riga che non
 * ha la forma attesa rende `null` per tutto l'elenco: un pezzo di classifica non e' una classifica.
 */
export function qcpListOf<T>(data: unknown, riga: (r: unknown) => r is T): T[] | null {
  const lista = Array.isArray(data) ? data : rec(data).data;
  return Array.isArray(lista) && lista.every(riga) ? (lista as T[]) : null;
}
/** Il corpo QCP di una risposta-OGGETTO: `{ provider, data: <corpo> }`. Per le liste c'e' `qcpListOf`. */
const corpoOf = (data: unknown): unknown => {
  const inner = rec(data).data;
  return typeof rec(data).provider === 'string' && inner !== null && typeof inner === 'object' && !Array.isArray(inner) ? inner : data;
};

export const qcpLeaderboardOf = (data: unknown) => qcpListOf(data, isEntry);
export const qcpMyMatchesOf = (data: unknown) => qcpListOf(data, isSummary);
export const qcpMatchOf = (data: unknown): MatchSummary | null => (isSummary(corpoOf(data)) ? (corpoOf(data) as MatchSummary) : null);
export function qcpReplayOf(data: unknown): MatchReplay | null {
  const o = corpoOf(data);
  const hands = rec(o).hands;
  return isSummary(o) && num(rec(o).setLength) && Array.isArray(hands) && hands.every(isHand) ? (o as MatchReplay) : null;
}
export function qcpInviteOf(data: unknown): QcpInvite | null {
  const o = rec(corpoOf(data));
  return str(o.code) && num(o.expiresAt) ? { code: o.code, expiresAt: o.expiresAt } : null;
}
export function qcpInvitePreviewOf(data: unknown): QcpInvitePreview | null {
  const o = rec(corpoOf(data));
  const c = rec(o.creator);
  return typeof c.displayName === 'string' && num(c.elo) && num(o.expiresAt) ? { creator: { displayName: c.displayName, elo: c.elo }, expiresAt: o.expiresAt } : null;
}
export function qcpAcceptedOf(data: unknown): QcpInviteAccepted | null {
  const o = rec(corpoOf(data));
  return str(o.matchId) && seatOf(o.seat) ? { matchId: o.matchId, seat: o.seat } : null;
}

/**
 * `chinesepoker.me.read`, dal `data` GIA' sceso (`capabilityPayloadOf(unwrapData(..))`): `{ hasProfile, user: { userId, ... } | null }`.
 * Parser CHIUSO sulla forma misurata (tma-orcb, 13/10 ~09:59Z, collaudo-b): `hasProfile` booleano obbligatorio; `true` ⇒ `user.userId`
 * stringa non vuota; `false` ⇒ `user` null (`false` con un `user` e' una forma rotta). Ogni altra forma e' `null` = «non disponibile».
 * ⚠️ `hasProfile:false` NON e' misurato: e' un'IPOTESI dal codice di limulo-app (`backend/resolvers.js`).
 */
export function qcpMeOf(data: unknown): QcpMe | null {
  const o = rec(data);
  if (typeof o.hasProfile !== 'boolean') return null;
  if (o.hasProfile) return str(rec(o.user).userId) ? { hasProfile: true, userId: rec(o.user).userId as string } : null;
  return o.user === null ? { hasProfile: false, userId: null } : null;
}
/**
 * Il MIO posto in una partita, o `null`: UNICO punto che lo decide (storico, replay, fine partita). Senza `mioUserId`, o con uno che
 * non e' nessuno dei due, nessuna affermazione: lo schermo resta com'e' senza `me`.
 */
export const postoMio = (players: MatchSummary['players'], mioUserId: string | null): Seat | null =>
  mioUserId === null ? null : players.A.userId === mioUserId ? 'A' : players.B.userId === mioUserId ? 'B' : null;

/* ── Errori ── */
export type QcpRestFailure = 'non-disponibile' | 'errore';
/**
 * `non-disponibile` SOLO per `424 E_QLASH_CAPABILITY_DISABLED` (la capability e' spenta): e' uno stato, non un guasto, e riprovare non
 * serve. Ogni altro esito (E_QLASH_HTTP_<n>, 424 RESOLVER_ERROR, rete) e' `errore`, con «Riprova»: il BFF non fa arrivare i codici
 * dell'app, quindi non c'e' altro da distinguere. Si legge `code`, mai `error` (e' il messaggio).
 */
export function qcpRestFailureOf(error: unknown): QcpRestFailure {
  const e = rec(error);
  return e.status === 424 && rec(e.data).code === 'E_QLASH_CAPABILITY_DISABLED' ? 'non-disponibile' : 'errore';
}
/** Lo status HTTP di un errore RTK, o `null` (rete, timeout, forma strana). */
export const qcpStatusOf = (error: unknown): number | null => (typeof rec(error).status === 'number' ? (rec(error).status as number) : null);

/**
 * La chiave di idempotenza di UNA azione dell'utente (8..200 caratteri, nel CORPO). La sceglie il client una volta e la RIUSA identica
 * a ogni nuovo tentativo della stessa azione: una nuova a ogni richiesta duplicherebbe l'invito se la prima era passata.
 */
export const qcpIdempotencyKey = (): string => `tma-${crypto.randomUUID()}`;
